import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { ColDef, ColGroupDef, GridApi } from "ag-grid-community";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import styled from "styled-components";

import TableWrapper from "components/AgGrid/TableWrapper";
import { ViewSelector } from "components/AgGrid/ViewSelector";
import { Tooltip } from "components/Form/FormikControls/Tooltip";
import { Row } from "components/Layout";
import { ReactSelect } from "components/SearchableSelector";

import { getActiveRole, getIsStaff } from "selectors";
import { getTableSetting } from "selectors/settings";

import { getDataExplorerRoute } from "lib/navigation";
import toast from "lib/toast";

import {
  type DatasetMeta,
  type MetaResponse,
  fetchColumnValues,
  fetchDatasets,
  fetchMeta,
} from "./api";
import { DataGrid } from "./DataGrid";
import { buildColumnDefs } from "./columnDefs/dynamic";

type DataScope = "staff" | "user" | "role";

interface ScopeOption {
  value: DataScope;
  label: string;
  tooltip: string;
}

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space[2]}px;
  padding: ${({ theme }) => theme.space[1]}px ${({ theme }) => theme.space[2]}px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray78};
  background: white;
  flex-shrink: 0;
`;

const TabButton = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  border-bottom: 2px solid
    ${({ $active, theme }) => ($active ? theme.colors.primary : "transparent")};
  padding: ${({ theme }) => theme.space[1]}px ${({ theme }) => theme.space[2]}px;
  cursor: pointer;
  font-family: Roboto, system-ui, sans-serif;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.gray40};
`;

const Spacer = styled.div`
  flex: 1;
`;

const ScopeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: Roboto, system-ui, sans-serif;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray40};
`;

const ScopeSelectContainer = styled.div`
  width: 150px;
`;

export function DataExplorer() {
  const isStaff = useSelector(getIsStaff);
  const activeRole = useSelector(getActiveRole);
  const { datasetId } = useParams<{ datasetId: string }>();
  const history = useHistory();

  const [datasets, setDatasets] = useState<DatasetMeta[]>([]);
  const [columnDefs, setColumnDefs] = useState<(ColDef | ColGroupDef)[]>([]);
  const [defaultSort, setDefaultSort] = useState<MetaResponse["default_sort"]>(
    [],
  );
  const [scope, setScope] = useState<DataScope>("role");
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const lastAppliedRef = useRef<typeof tableSetting>(null);

  const tableName = datasetId ? `dataexplorer_${datasetId}` : "";
  const tableSetting = useSelector(getTableSetting(tableName || "__none__"));

  const roleSlug = activeRole?.slug;
  const roleName = activeRole?.name ?? activeRole?.slug ?? "your role";

  const scopeOptions: ScopeOption[] = useMemo(() => {
    const roleOptions: ScopeOption[] = [
      {
        value: "role",
        label: "Role",
        tooltip: `Show only data visible to your current role (${roleName}).`,
      },
      {
        value: "user",
        label: "User",
        tooltip: "Show all data visible to you across all your AgriNous roles.",
      },
    ];
    if (isStaff) {
      roleOptions.push({
        value: "staff",
        label: "Staff",
        tooltip: "Show ALL data available system wide.",
      });
    }
    return roleOptions;
  }, [isStaff, roleName]);

  // Fetch dataset list on mount; roleSlug dep is a ready guard, not a re-run trigger.
  useEffect(() => {
    if (!roleSlug) return;
    let ignore = false;
    fetchDatasets(roleSlug)
      .then(({ datasets: ds }) => {
        if (!ignore) setDatasets(ds);
      })
      .catch(() => {
        if (!ignore) toast.error("Failed to load datasets.");
      });
    return () => {
      ignore = true;
    };
  }, [roleSlug]);

  // Redirect to the first dataset when none is selected (e.g. initial load).
  useEffect(() => {
    if (!datasetId && datasets.length > 0) {
      history.replace(getDataExplorerRoute(datasets[0].id));
    }
  }, [datasetId, datasets, history]);

  // Cache per field so AG Grid's Set Filter values getter (called every time a filter
  // panel opens) doesn't re-fetch — each unnecessary refresh risks AG Grid collapsing
  // a narrowed selection back to "everything selected". Recreated only when the
  // dataset/role/scope changes, matching columnDefs' own recompute cadence below.
  const fetchValues = useMemo(() => {
    const cache = new Map<string, Promise<unknown[]>>();
    return (colId: string) => {
      let cached = cache.get(colId);
      if (!cached) {
        cached = fetchColumnValues(
          datasetId,
          colId,
          roleSlug ?? "",
          scope,
        ).then(r => r.values);
        cache.set(colId, cached);
      }
      return cached;
    };
  }, [datasetId, roleSlug, scope]);

  // Fetch column schema whenever the active dataset changes.
  // Note: meta.default_sort is available here but not yet applied — saved views take
  // precedence when present, and without one the grid uses server-side ordering.
  // To apply it, store it alongside columnDefs and pass it into the view-application effect.
  useEffect(() => {
    if (!datasetId || !roleSlug) return;
    let ignore = false;
    setColumnDefs([]);
    fetchMeta(datasetId, roleSlug)
      .then(meta => {
        if (!ignore) {
          setColumnDefs(buildColumnDefs(meta.columns, fetchValues));
          setDefaultSort(meta.default_sort);
        }
      })
      .catch(() => {
        if (!ignore) toast.error("Failed to load dataset columns.");
      });
    return () => {
      ignore = true;
    };
  }, [datasetId, roleSlug, scope, fetchValues]);

  // Apply a saved view once both the grid API and column defs are ready.
  // Also re-runs when the user switches views via the ViewSelector.
  // lastAppliedRef guards against re-applying the same view when an unrelated dep changes —
  // without it the effect would refire whenever columnDefs updates (e.g. after a dataset fetch).
  useEffect(() => {
    if (!gridApi || !columnDefs.length) return;
    if (tableSetting === lastAppliedRef.current) return;
    lastAppliedRef.current = tableSetting;
    if (tableSetting?.state?.length) {
      gridApi.applyColumnState({
        state: tableSetting.state,
        applyOrder: true,
        defaultState: { rowGroup: false, hide: true },
      });
      gridApi.setFilterModel(tableSetting.filters ?? {});
    } else {
      gridApi.resetColumnState();
      gridApi.setFilterModel({});
      if (defaultSort.length) {
        gridApi.applyColumnState({
          state: defaultSort.map(s => ({
            colId: s.col_id,
            sort: s.sort as "asc" | "desc",
          })),
          defaultState: { sort: null },
        });
      }
    }
  }, [gridApi, columnDefs, tableSetting, defaultSort]);

  const handleGridReady = useCallback((api: GridApi) => {
    setGridApi(api);
    // Reset so the view-application effect fires again on the new grid instance
    lastAppliedRef.current = null;
  }, []);

  // TODO: DataExplorer is currently staff-only. When opened to non-staff users,
  // remove this guard and wire up the non-staff scopeOptions path (user/role).
  if (!isStaff) return null;

  return (
    <Shell>
      <Toolbar>
        {datasets.map(ds => (
          <TabButton
            key={ds.id}
            $active={ds.id === datasetId}
            onClick={() => history.push(getDataExplorerRoute(ds.id))}
          >
            {ds.label}
          </TabButton>
        ))}
        <Spacer />
        {datasetId && <ViewSelector tableName={tableName} isDirty={false} />}
        {scopeOptions.length > 1 && (
          <ScopeWrapper>
            Scope
            <ScopeSelectContainer>
              <ReactSelect
                value={scopeOptions.find(o => o.value === scope) ?? null}
                options={scopeOptions}
                onChange={(opt: ScopeOption | null) => {
                  if (opt) setScope(opt.value);
                }}
                isSearchable={false}
                menuPortalTarget={document.body}
                formatOptionLabel={(opt: ScopeOption) => (
                  <Row justifyBetween alignCenter fullWidth>
                    <span>{opt.label}</span>
                    <Tooltip title={opt.tooltip} placement="right" />
                  </Row>
                )}
              />
            </ScopeSelectContainer>
          </ScopeWrapper>
        )}
      </Toolbar>
      {datasetId && (
        <TableWrapper>
          <DataGrid
            datasetId={datasetId}
            roleSlug={roleSlug ?? ""}
            scope={scope}
            columnDefs={columnDefs}
            onGridReady={handleGridReady}
          />
        </TableWrapper>
      )}
    </Shell>
  );
}

export default DataExplorer;
